import { ReactNode } from 'react';
import {
  Body1Strong,
  Link as FluentLink,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from '@fluentui/react-components';

import { Bubble, DateLabel, QueryLink, TabContent } from '@shared/components';
import { AvatarGroup } from '@modules/users';

/**
 * Shared version-history table. Callers pre-shape `rows` and optionally configure an Approval
 * column or slot-based extensions. Extension points: `approval`, `extraRowsAfter`, `footer`.
 * If a future caller needs arbitrary extra columns beyond Approval, add `extraColumns` then.
 */
export type THistoryRow = {
  /** 'draft' or a version id like '1.2'. Drives the link label: 'Draft' for draft, 'v{id}' otherwise. */
  id: string;
  /** Empty string allowed for the draft row (renders as '-'). */
  note: string;
  /** Empty string allowed for the draft row (renders as '-'). */
  date: string;
  authorIds: string[];
};

type TProps = {
  rows: THistoryRow[];
  versionedLabel: 'Versioned' | 'Uploaded';
  versionColumnWidth?: number;
  onRowClick: (row: THistoryRow) => void;
  emptyMessage?: string;
  approval?: { renderCell: (row: THistoryRow) => ReactNode };
  extraRowsAfter?: ReactNode;
  footer?: ReactNode;
};

const labelFor = (row: THistoryRow): string => (row.id === 'draft' ? 'Draft' : `v${row.id}`);

const HistoryTable = ({
  rows,
  versionedLabel,
  versionColumnWidth = 80,
  onRowClick,
  emptyMessage = 'No versions exist yet.',
  approval,
  extraRowsAfter,
  footer,
}: TProps) => {
  if (rows.length === 0 && !extraRowsAfter && !footer) {
    return (
      <TabContent>
        <p className="text-neutral-500">{emptyMessage}</p>
      </TabContent>
    );
  }

  return (
    <TabContent>
      <Bubble>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: versionColumnWidth }}>
                <Body1Strong>Version</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell>
                <Body1Strong>Change note</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell style={{ width: '100px' }}>
                <Body1Strong>{versionedLabel}</Body1Strong>
              </TableHeaderCell>
              <TableHeaderCell style={{ width: '100px' }}>
                <Body1Strong>Authors</Body1Strong>
              </TableHeaderCell>
              {approval && (
                <TableHeaderCell style={{ width: '110px' }}>
                  <Body1Strong>Approval</Body1Strong>
                </TableHeaderCell>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <QueryLink params={{ view: row.id }}>
                    <FluentLink onClick={() => onRowClick(row)}>{labelFor(row)}</FluentLink>
                  </QueryLink>
                </TableCell>
                <TableCell>
                  {row.note ? (
                    <Tooltip relationship="description" content={<p className="whitespace-break-spaces">{row.note}</p>}>
                      <span className="block max-w-full whitespace-nowrap text-ellipsis overflow-hidden">
                        {row.note}
                      </span>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{row.date ? <DateLabel short>{row.date}</DateLabel> : '-'}</TableCell>
                <TableCell>
                  <AvatarGroup ids={row.authorIds} />
                </TableCell>
                {approval && <TableCell>{approval.renderCell(row)}</TableCell>}
              </TableRow>
            ))}
            {extraRowsAfter}
          </TableBody>
        </Table>
        {footer}
      </Bubble>
    </TabContent>
  );
};

export default HistoryTable;
